import React, { useEffect, useRef, useState } from 'react';
import { Alert, Slide, SlideProps, Snackbar, SnackbarCloseReason } from '@mui/material';
import { useRecoilState } from 'recoil';
import { ToastMessageState } from '../../recoil/states';

const FIXED_BAR_ID = 'divFiexdRia';

const ToastMessage = () => {
    const [toastMessageState, setToastMessageState] = useRecoilState(ToastMessageState);
    const [bottomOffset, setBottomOffset] = useState(0);

    const fixedElRef = useRef<HTMLElement | null>(null);
    const resizeObsRef = useRef<ResizeObserver | null>(null);

    function SlideTransition(props: SlideProps) {
        return <Slide {...props} direction="up" />;
    }

    const measure = (el: HTMLElement | null) => {
        if (!el) {
            setBottomOffset(0);
            return;
        }
        const cs = getComputedStyle(el);
        const hidden = cs.display === 'none' || cs.visibility === 'hidden';
        setBottomOffset(hidden ? 0 : el.offsetHeight || 0);
    };

    const attachResizeObserver = (el: HTMLElement | null) => {
        // 정리
        if (resizeObsRef.current) {
            resizeObsRef.current.disconnect();
            resizeObsRef.current = null;
        }
        fixedElRef.current = el;

        if (!el) {
            setBottomOffset(0);
            return;
        }

        // 최초 측정 + 크기 변경 추적
        measure(el);
        const ro = new ResizeObserver(() => measure(el));
        ro.observe(el);
        resizeObsRef.current = ro;
    };

    // 라우팅/DOM 변화 중 #divFiexdRia 출현·제거 감지
    useEffect(() => {
        const findAndAttach = () => {
            const el = document.getElementById(FIXED_BAR_ID) as HTMLElement | null;
            if (el !== fixedElRef.current) attachResizeObserver(el);
            else measure(el);
        };

        // 최초 시도
        findAndAttach();

        const mo = new MutationObserver(() => {
            // DOM 구조 변화 시 재시도
            findAndAttach();
        });
        mo.observe(document.body, { childList: true, subtree: true });

        const onResize = () => measure(fixedElRef.current);
        window.addEventListener('resize', onResize);

        return () => {
            mo.disconnect();
            window.removeEventListener('resize', onResize);
            if (resizeObsRef.current) resizeObsRef.current.disconnect();
        };
    }, []);

    // 토스트가 열릴 때도 한 번 더 보정 (빠른 전환 대비)
    useEffect(() => {
        if (toastMessageState.isShow) {
            const el = document.getElementById(FIXED_BAR_ID) as HTMLElement | null;
            attachResizeObserver(el);
        }
    }, [toastMessageState.isShow]);

    const handleClose = (
        event?: React.SyntheticEvent | Event,
        reason?: SnackbarCloseReason,
    ) => {
        if (reason === 'clickaway') return;
        setToastMessageState(raw => ({ ...raw, isShow: false }));
    };

    return (
        <Snackbar
            open={toastMessageState.isShow}
            autoHideDuration={3000}
            onClose={handleClose}
            TransitionComponent={SlideTransition}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            // 하단 고정 바 높이 + 안전영역 + 기본 여백
            sx={{
                bottom: `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px) + 16px)`,
            }}
            key={Math.random()}
        >
            <Alert onClose={handleClose} severity="success" variant="filled" sx={{ width: '100%' }}>
                {toastMessageState.message}
            </Alert>
        </Snackbar>
    );
};

export default ToastMessage;
